import React, { useState, useRef, useEffect } from 'react';

const GridHeader = ({ header, isColumn, onUpdate, onDelete, editorMode, className = '' }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [title, setTitle] = useState(header.title || '');
  const [horizon, setHorizon] = useState(header.horizon || '');
  const [isHovered, setIsHovered] = useState(false);
  
  const inputRef = useRef(null);
  
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);
  
  const handleEdit = () => {
    if (editorMode === 'cell') {
      setIsEditing(true);
    }
  };
  
  const handleSave = () => {
    if (isColumn) {
      onUpdate(title);
    } else {
      onUpdate({ title, horizon });
    }
    setIsEditing(false);
  };
  
  const handleCancel = () => {
    setTitle(header.title);
    setHorizon(header.horizon || '');
    setIsEditing(false);
  };
  
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    }
  };
  
  // Gérer le survol
  const handleMouseEnter = () => {
    setIsHovered(true);
  };
  
  const handleMouseLeave = () => {
    setIsHovered(false);
  };
  
  // Déterminer si l'en-tête est obligatoire
  const isObligatory = header.isObligatory || false;
  const isPseudoFunction = header.isPseudoFunction || false;
  
  // Classe conditionnelle pour les en-têtes obligatoires ou pseudo-fonctions
  const headerTypeClass = isObligatory ? 'obligatory' : '';
  const pseudoFunctionClass = isPseudoFunction ? 'pseudo-function' : '';
  
  return (
    <div 
      className={`grid-header-cell ${isColumn ? 'column-header' : 'row-header'} ${headerTypeClass} ${pseudoFunctionClass} ${className}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      data-id={isColumn ? `header-col-${header.id}` : `header-row-${header.id}`}
    >
      {isEditing ? (
        <div className="header-edit-form">
          <input
            ref={inputRef}
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={handleKeyDown}
            className="header-input"
            placeholder={isColumn ? "Titre de la fonction" : "Titre de l'horizon"}
          />
          
          {!isColumn && (
            <input
              type="text"
              value={horizon}
              onChange={(e) => setHorizon(e.target.value)}
              onKeyDown={handleKeyDown}
              className="header-input"
              placeholder="Code horizon (ex: H1)"
            />
          )}
          
          <div className="header-edit-buttons">
            <button onClick={handleSave} className="header-button confirm">✓</button>
            <button onClick={handleCancel} className="header-button cancel">✗</button>
          </div>
        </div>
      ) : (
        <div className="header-content">
          <div className="header-title">
            {title}
            {title && title.length > 20 && (
              <div className="header-title-tooltip">{title}</div>
            )}
          </div>
          {!isColumn && <div className="header-subtitle">{horizon}</div>}
          
          {editorMode === 'cell' && (
            <div className={`header-action-buttons ${isHovered ? 'visible' : ''}`}>
              <button onClick={handleEdit} className="header-button edit" title="Modifier">
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 3a2.85 2.85 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"></path>
                </svg>
              </button>
              
              {/* Ne pas afficher le bouton de suppression pour les headers obligatoires ou fixes */}
              {!header.isFixed && (
                <button onClick={onDelete} className="header-button delete" title="Supprimer">
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 6h18"></path>
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                  </svg>
                </button>
              )}
            </div>
          )}
        </div>
      )}
      
      {/* Indicateur pour les colonnes obligatoires */}
      {isObligatory && !isEditing && (
        <div className="obligatory-indicator" title="Fonction obligatoire">*</div>
      )}
      
      {/* Indicateur pour les pseudo-fonctions */}
      {isPseudoFunction && !isEditing && (
        <div className="pseudo-function-indicator" title="Pseudo-fonction">P</div>
      )}
    </div>
  );
}

export default GridHeader; 